import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchSettings } from '@/services/api';

const quickLinks = [
  { label: 'Home', path: '/' },
  { label: 'About Us', path: '/about' },
  { label: 'Products', path: '/products' },
  { label: 'Brand Story', path: '/brand-story' },
  { label: 'Blog', path: '/blog' },
  { label: 'Contact', path: '/contact' },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [settings, setSettings] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchSettings().then(setSettings).catch(() => {});
  }, []);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    const body = new URLSearchParams({ email });
    try {
      await fetch('https://readdy.ai/api/form/d7afp21nauq9gnuqnhtg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubscribed(true);
      setEmail('');
    } catch {
      setSubscribed(true);
    }
  };

  const phone = settings.contact_phone || '+86 137 9029 6151';
  const phoneHref = 'tel:' + phone.replace(/[\s()-]/g, '');
  const emailAddress = settings.contact_email || 'andy-zou@qzyunxin.com';
  const address = settings.contact_address || "No.6 Sunshine Road, Xiamei Town, Nan'an City, Fujian Province, China";
  const description = settings.company_description || 'Crafting premium tool bags and organizer bags with precision and passion. Quality you can trust, style you can feel.';
  const copyright = settings.company_copyright || 'Quanzhou Yunxin Outdoor Products Co., Ltd.';

  return (
    <footer className="bg-stone-900 text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <img
              src="https://static.readdy.ai/image/54d0c5d0466a0c807f4c52375a988d35/2dfcd75db5398e3e348be2319053c6c3.png"
              alt="Yunxin Logo"
              className="h-12 w-auto object-contain mb-4 brightness-0 invert"
            />
            <p className="text-stone-400 text-sm leading-relaxed">
              {description}
            </p>
            <div className="flex gap-3 mt-5">
              {['ri-linkedin-fill', 'ri-facebook-fill', 'ri-instagram-line', 'ri-youtube-fill'].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  rel="nofollow"
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-700 hover:bg-amber-700 transition-colors duration-300 cursor-pointer"
                >
                  <i className={`${icon} text-sm`} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs font-semibold tracking-widest uppercase text-stone-400 mb-5">
              <a href="#quick-links" id="quick-links">Quick Links</a>
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-stone-300 hover:text-white text-sm transition-colors duration-200 cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs font-semibold tracking-widest uppercase text-stone-400 mb-5">
              <a href="#contact-info" id="contact-info">Contact Info</a>
            </h4>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-map-pin-line text-amber-500 text-sm" />
                </div>
                <span className="text-stone-300 text-sm leading-relaxed">
                  {address}
                </span>
              </li>
              <li className="flex gap-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line text-amber-500 text-sm" />
                </div>
                <a href={phoneHref} rel="nofollow" className="text-stone-300 text-sm hover:text-white transition-colors">
                  {phone}
                </a>
              </li>
              <li className="flex gap-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-line text-amber-500 text-sm" />
                </div>
                <a href={`mailto:${emailAddress}`} rel="nofollow" className="text-stone-300 text-sm hover:text-white transition-colors">
                  {emailAddress}
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-xs font-semibold tracking-widest uppercase text-stone-400 mb-5">
              <a href="#newsletter" id="newsletter">Newsletter</a>
            </h4>
            <p className="text-stone-400 text-sm mb-4">
              Stay updated with our latest products and news.
            </p>
            {subscribed ? (
              <p className="text-amber-400 text-sm font-medium">Thank you for subscribing!</p>
            ) : (
              <form onSubmit={handleSubscribe} data-readdy-form className="flex flex-col gap-3">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="bg-stone-800 border border-stone-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-stone-500 focus:outline-none focus:border-amber-600 transition-colors"
                />
                <button
                  type="submit"
                  className="bg-amber-700 hover:bg-amber-600 text-white text-xs font-semibold tracking-widest uppercase px-5 py-2.5 rounded-lg transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  Subscribe
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-stone-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-stone-500 text-xs">
            &copy; {new Date().getFullYear()} {copyright} All rights reserved.
          </p>
          <p className="text-stone-600 text-xs">
            Crafted with precision in Fujian, China
          </p>
        </div>
      </div>
    </footer>
  );
}
